/* Offline-queue voor kritieke mutaties (presentie, daglog).
 *
 * Ontwerp:
 * - We slaan queued mutaties op in IndexedDB (single object store `pending_ops`).
 * - Elke op krijgt een `id` (uuid) die als **Idempotency-Key** naar de backend
 *   gaat. Bij replay krijgt de backend dezelfde key → dedupe daar.
 * - Enqueue's gebeuren via `queueOp({ method, url, data, label })`. De helper
 *   probeert direct als je online bent, en queued anders.
 * - `flushQueue()` wordt automatisch aangeroepen bij het `online`-event én bij
 *   applicatie-start. Progress-events dispatchen `window`-event `offline-flush`.
 */
import api from "@/lib/api";

const DB_NAME = "np-offline";
const DB_VERSION = 1;
const STORE = "pending_ops";

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("no-indexeddb"));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const s = db.createObjectStore(STORE, { keyPath: "id" });
        s.createIndex("created_at", "created_at");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function uuidv4() {
  // Voldoende voor idempotency (niet crypto-sensitive)
  if (crypto?.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

async function txDo(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result;
    Promise.resolve(fn(store)).then((r) => { result = r; }).catch(reject);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("tx-aborted"));
  });
}

export async function enqueueOp(op) {
  const record = {
    id: uuidv4(),
    method: op.method || "POST",
    url: op.url,
    data: op.data ?? null,
    label: op.label || op.url,
    attempts: 0,
    last_error: null,
    created_at: new Date().toISOString(),
  };
  await txDo("readwrite", (s) => s.add(record));
  window.dispatchEvent(new CustomEvent("offline-queue-changed"));
  return record.id;
}

export async function listPending() {
  return txDo("readonly", (s) => new Promise((res, rej) => {
    const req = s.getAll();
    req.onsuccess = () => res(req.result || []);
    req.onerror = () => rej(req.error);
  }));
}

export async function pendingCount() {
  const items = await listPending();
  return items.length;
}

async function _removeById(id) {
  await txDo("readwrite", (s) => s.delete(id));
}

async function _updateRecord(rec) {
  await txDo("readwrite", (s) => s.put(rec));
}

async function _replayOne(rec) {
  const config = {
    method: rec.method || "POST",
    url: rec.url,
    data: rec.data ?? undefined,
    headers: { "Idempotency-Key": rec.id },
  };
  const res = await api.request(config);
  return res;
}

let flushing = false;

/** Probeer de queue leeg te maken. Retourneert {sent, failed, remaining}. */
export async function flushQueue() {
  if (flushing) return { sent: 0, failed: 0, remaining: await pendingCount() };
  flushing = true;
  try {
    const items = await listPending();
    let sent = 0, failed = 0;
    for (const rec of items) {
      try {
        await _replayOne(rec);
        await _removeById(rec.id);
        sent += 1;
        window.dispatchEvent(new CustomEvent("offline-queue-changed"));
      } catch (e) {
        // 4xx (behalve 408/429) markeren we als "vergif" en verwijderen: server
        // heeft de request bewust geweigerd, oneindig replayen levert niks op.
        const status = e?.response?.status;
        if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
          await _removeById(rec.id);
          failed += 1;
          window.dispatchEvent(new CustomEvent("offline-queue-poison", {
            detail: { rec, status, detail: e?.response?.data?.detail },
          }));
        } else {
          rec.attempts += 1;
          rec.last_error = e?.message || String(e);
          await _updateRecord(rec);
          failed += 1;
          break;  // Netwerk-fout: stop de flush, probeer straks weer.
        }
      }
    }
    return { sent, failed, remaining: await pendingCount() };
  } finally {
    flushing = false;
  }
}

/** High-level helper: probeer direct te versturen, queue on failure. */
export async function submitOrQueue(op) {
  const idempKey = uuidv4();
  const config = {
    method: op.method || "POST",
    url: op.url,
    data: op.data,
    headers: { "Idempotency-Key": idempKey },
  };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    // Direct queueen; behoud de key als queue-id zodat replay ook dedupt.
    await txDo("readwrite", (s) => s.add({
      id: idempKey,
      method: config.method,
      url: config.url,
      data: config.data ?? null,
      label: op.label || op.url,
      attempts: 0,
      last_error: null,
      created_at: new Date().toISOString(),
    }));
    window.dispatchEvent(new CustomEvent("offline-queue-changed"));
    return { queued: true, id: idempKey };
  }
  try {
    const r = await api.request(config);
    return { queued: false, data: r.data };
  } catch (e) {
    // Bij netwerk-fout queueen we
    const isNetErr = !e?.response;
    if (isNetErr) {
      await txDo("readwrite", (s) => s.add({
        id: idempKey,
        method: config.method,
        url: config.url,
        data: config.data ?? null,
        label: op.label || op.url,
        attempts: 1,
        last_error: e?.message || "network",
        created_at: new Date().toISOString(),
      }));
      window.dispatchEvent(new CustomEvent("offline-queue-changed"));
      return { queued: true, id: idempKey };
    }
    throw e;
  }
}

/** Registreer de auto-flush luisteraars. Aanroepen bij app-start. */
export function registerOfflineFlush() {
  if (typeof window === "undefined") return;
  if (window.__np_offline_registered) return;
  window.__np_offline_registered = true;

  const tryFlush = () => {
    if (navigator.onLine === false) return;
    flushQueue().catch(() => { /* silent */ });
  };
  window.addEventListener("online", tryFlush);
  window.addEventListener("focus", tryFlush);
  // Startup flush (bij navigatie vanaf hydration)
  setTimeout(tryFlush, 1500);
}
